import React, { useContext, useEffect, useReducer, useRef, useState } from 'react';

import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { useMediaQuery } from '@mui/material';

import { Phone, MapPin, Globe, Share2 } from 'lucide-react';

import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import SettingsIcon from '@mui/icons-material/Settings';
import ViewCarouselIcon from '@mui/icons-material/ViewCarousel';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { array, object, string, ValidationError } from 'yup';

import Axios from '../../../../Assets/Axios/Axios';

import ToastContext from '../../../../Context/ToastContext';

import { uploadFile, UPLOAD_FOLDERS } from '../../../../Services/S3';

import { COLORS } from '../../../../Constant/COLORS';

import { TypoPoppins, TypoSerif } from '../../../../Components/Typo';

import { GetCategories } from '../../../../Server/Query';

import ImageInput from './ImageInput';
import { useFormContext } from './FormContext';

const MAX_NO_OF_IMGS = 6;

const JobInformation = () => {
  const { showToastMessage } = useContext(ToastContext);
  const {
    serviceInputs,
    updateServiceInputs,
    servicePackages,
    imageFiles,
    uploadedUrls,
    nextStep,
  } = useFormContext();

  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'));

  const [isUploading, setIsUploading] = useState(false);
  const [images, setImages] = useState([]);
  // At least one bookable package per service. Pre-filled on re-entry (Edit flow).
  const [packages, setPackages] = useState(() =>
    servicePackages.length
      ? servicePackages.map(p => ({ ...p }))
      : [{ name: '', price: '', description: '' }]
  );
  const [packageErrors, setPackageErrors] = useState([{}]);

  function handlePackageChange(index, field, value) {
    setPackages(current =>
      current.map((pkg, i) => (i === index ? { ...pkg, [field]: value } : pkg))
    );
  }

  function addPackage() {
    setPackages(current => [...current, { name: '', price: '', description: '' }]);
    setPackageErrors(current => [...current, {}]);
  }

  function removePackage(index) {
    setPackages(current => current.filter((_, i) => i !== index));
    setPackageErrors(current => current.filter((_, i) => i !== index));
  }

  // Validate every package row; returns the cleaned list or null if invalid.
  function validatePackages() {
    const errs = packages.map(pkg => {
      const rowErrors = {};
      if (!pkg.name?.trim()) rowErrors.name = 'Package name is required';
      const price = Number(pkg.price);
      if (pkg.price === '' || Number.isNaN(price) || price <= 0)
        rowErrors.price = 'Enter a valid price';
      return rowErrors;
    });
    setPackageErrors(errs);
    if (errs.some(e => Object.keys(e).length)) return null;
    return packages.map(pkg => ({
      name: pkg.name.trim(),
      price: Number(pkg.price),
      description: pkg.description?.trim() || null,
    }));
  }
  const [errors, updateErrors] = useReducer(errorReducer, {
    categoryId: null,
    location: null,
    hotlineNumber: null,
    website: null,
    facebookURL: null,
    description: null,
    images: null,
  });

  const imgInputRef = useRef();

  const srvAvatarImgRef = useRef();
  const srvBannerImgRef = useRef();
  const serviceImages = useRef([]).current;

  const [categories, setCategories] = useState([]);

  useEffect(() => {
    Axios(GetCategories).then(({ data, errors }) => {
      if (errors || !data?.categories) return undefined;
      const sorted = [...data.categories].sort((a, b) => {
        if (a.slug === 'other') return 1;
        if (b.slug === 'other') return -1;
        return a.name.localeCompare(b.name);
      });
      setCategories(sorted);
    });
  }, []);

  function handleChange(event) {
    const { name, value } = event.target;
    updateServiceInputs({ [name]: value });
  }

  // Selecting a category sets both categoryId (FK) and a readable service name.
  function handleChangeCategory(event) {
    const categoryId = event.target.value;
    const category = categories.find(c => c.id === categoryId);
    updateServiceInputs({ categoryId, name: category?.name ?? '' });
  }

  function handleChangeImgInput({ target: { files } }) {
    if (images.length + files.length > MAX_NO_OF_IMGS) {
      showToastMessage(
        `Sorry, you can upload upto ${MAX_NO_OF_IMGS} images only`,
        'info'
      );
      imgInputRef.current.value = '';
      return undefined;
    }

    serviceImages.push(...Array.from(files));
    const imageUrls = Array.from(files).map(image =>
      URL.createObjectURL(image)
    );

    setImages(currentState => [...currentState, ...imageUrls]);
  }

  async function handleSubmit(event) {
    event.preventDefault();
    updateErrors({ type: ERROR_ACTION.CLEAR });

    try {
      await schema.validate(
        { ...serviceInputs, images: serviceImages },
        { abortEarly: false }
      );
    } catch (error) {
      if (error instanceof ValidationError) {
        const errors = error.inner.reduce(
          (errors, err) => ({ ...errors, [err.path]: err.message }),
          {}
        );
        updateErrors({ type: ERROR_ACTION.SET, payload: errors });
        return undefined;
      }
      console.error(error);
      return undefined;
    }

    // Validate the service packages before uploading anything.
    const validatedPackages = validatePackages();
    if (!validatedPackages) return undefined;

    imageFiles.icon = srvAvatarImgRef.current?.files?.[0] ?? null;
    imageFiles.serviceImages = serviceImages;
    imageFiles.bannerImage = srvBannerImgRef.current?.files?.[0] ?? null;

    // Upload the service images to S3 now, before moving to Confirmation, and keep
    // the URLs. The professional avatar + KYC docs were already uploaded at the
    // Personal Details step. The final "Continue" then only creates the DB rows
    // with these URLs — no uploads happen at submit time.
    setIsUploading(true);
    try {
      const [serviceImageUrls, icon, bannerImage] = await Promise.all([
        Promise.all(
          (imageFiles.serviceImages ?? []).map(file =>
            uploadFile(file, UPLOAD_FOLDERS.SERVICE_GALLERY)
          )
        ),
        imageFiles.icon
          ? uploadFile(imageFiles.icon, UPLOAD_FOLDERS.SERVICE_ICON)
          : null,
        imageFiles.bannerImage
          ? uploadFile(imageFiles.bannerImage, UPLOAD_FOLDERS.SERVICE_BANNER)
          : null,
      ]);

      Object.assign(uploadedUrls, {
        serviceImages: serviceImageUrls,
        icon,
        bannerImage,
      });

      // Persist the validated packages for the Confirmation step (mutate in place
      // so the shared context array reference stays intact).
      servicePackages.length = 0;
      servicePackages.push(...validatedPackages);

      nextStep();
    } catch (uploadError) {
      console.error('[registration] image upload to S3 failed', uploadError);
      showToastMessage(
        'Image upload failed. Please check your connection and try again.',
        'error'
      );
    } finally {
      setIsUploading(false);
    }
  }

  function getProps(name) {
    return {
      name,
      value: serviceInputs[name],
      onChange: handleChange,
      helperText: errors[name],
      error: Boolean(errors[name]),
      inputProps: { spellCheck: false },
    };
  }

  return (
    <Container
      maxWidth='sm'
      component='form'
      onSubmit={handleSubmit}
      sx={{ my: 8 }}
    >
      <TypoSerif textAlign='center' variant='h4' mb={2}>
        Service Details
      </TypoSerif>

      <Box display='grid' gridTemplateColumns='1fr 1fr' gap={2}>
        <Stack gap={1} alignItems='center'>
          <ImageInput
            ref={srvAvatarImgRef}
            thumbnail={SettingsIcon}
            tooltip='Service Icon'
            avatarProps={{ sx: { borderRadius: { xs: '1em', sm: '999px' } } }}
          />
          {isMobile && (
            <span style={{ textAlign: 'center' }}>Service Icon</span>
          )}
        </Stack>

        <Stack gap={1} alignItems='center'>
          <ImageInput
            ref={srvBannerImgRef}
            thumbnail={ViewCarouselIcon}
            tooltip='Service Banner Image'
            avatarProps={{ sx: { borderRadius: { xs: '1em', sm: '999px' } } }}
          />
          {isMobile && (
            <span style={{ textAlign: 'center' }}>Service Banner Image</span>
          )}
        </Stack>

        <TextField
          sx={{ gridColumn: { xs: 'span 2', sm: 'unset' } }}
          select
          size='small'
          fullWidth
          value={serviceInputs.categoryId ?? ''}
          onChange={handleChangeCategory}
          error={Boolean(errors.categoryId)}
          name='categoryId'
          helperText={errors.categoryId}
          SelectProps={{
            displayEmpty: true,
            renderValue: value => {
              const category = categories.find(c => c.id === value);
              return (
                category?.name || (
                  <span style={{ color: COLORS.grey6 }}>Category</span>
                )
              );
            },
          }}
        >
          {categories.map(category => (
            <MenuItem value={category.id} key={category.id}>
              {category.name}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          sx={{ gridColumn: { xs: 'span 2', sm: 'unset' } }}
          fullWidth
          placeholder='Hotline Number'
          type='tel'
          {...getProps('hotlineNumber')}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <Phone size={17} color={COLORS.inkSoft} strokeWidth={1.8} />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          sx={{ gridColumn: { xs: 'span 2', sm: 'unset' } }}
          fullWidth
          placeholder='Service location'
          {...getProps('location')}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <MapPin size={17} color={COLORS.inkSoft} strokeWidth={1.8} />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          sx={{ gridColumn: { xs: 'span 2', sm: 'unset' } }}
          fullWidth
          autoComplete='url'
          placeholder='Service Website URL'
          inputMode='url'
          {...getProps('websiteUrl')}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <Globe size={17} color={COLORS.inkSoft} strokeWidth={1.8} />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          sx={{ gridColumn: { xs: 'span 2', sm: 'unset' } }}
          fullWidth
          autoComplete='url'
          inputMode='url'
          placeholder='Service Social Media URL'
          {...getProps('facebookUrl')}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <Share2 size={17} color={COLORS.inkSoft} strokeWidth={1.8} />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          sx={{ gridColumn: 'span 2' }}
          fullWidth
          multiline
          rows={3}
          placeholder='Service Description'
          InputProps={{
            endAdornment: (
              <InputAdornment position='end' sx={{ alignSelf: 'end', mb: 1 }}>
                <TypoPoppins color='text.secondary'>
                  {serviceInputs.description.length || ''}
                </TypoPoppins>
              </InputAdornment>
            ),
          }}
          {...getProps('description')}
        />

        <Box gridColumn='span 2' width={1}>
          <Box
            boxSizing='border-box'
            p={2}
            overflow='hidden'
            width='inherit'
            display='grid'
            gridTemplateColumns={{
              xs: 'repeat(2,1fr)',
              sm: 'repeat(3,1fr)',
            }}
            border={`1px solid ${COLORS.grey6}`}
            borderRadius={1}
            gap={2}
          >
            {images.map(image => (
              <img
                key={image}
                src={image}
                style={{
                  minWidth: 0,
                  width: '100%',
                  height: 'auto',
                  objectFit: 'cover',
                  aspectRatio: '3/2',
                  maxHeight: '200px',
                  borderRadius: 4,
                }}
                alt='Preview'
              />
            ))}

            {images.length < MAX_NO_OF_IMGS && (
              <Box textAlign='center' sx={{ aspectRatio: '3/2' }}>
                <IconButton
                  aria-label='Add Service Images'
                  sx={{ width: 'auto', height: '100%' }}
                  onClick={() => imgInputRef.current.click()}
                >
                  <AddPhotoAlternateIcon
                    sx={{ width: 'inherit', height: 'inherit' }}
                    htmlColor={COLORS.black}
                  />
                </IconButton>
              </Box>
            )}
          </Box>
          {errors.images && (
            <FormHelperText error sx={{ ml: 1.5 }}>
              {errors.images}
            </FormHelperText>
          )}

          <FormHelperText>
            You can insert upto {MAX_NO_OF_IMGS} service images
          </FormHelperText>
        </Box>
      </Box>
      <input
        onChange={handleChangeImgInput}
        ref={imgInputRef}
        type='file'
        accept='.jpg,.jpeg,.png'
        id='imageUpload'
        style={{ display: 'none', opacity: 0, visibility: 'hidden' }}
        multiple
      />

      <Box mt={4}>
        <TypoPoppins fontWeight={600} mb={1}>
          Service Packages
        </TypoPoppins>
        <FormHelperText sx={{ mb: 2 }}>
          Add at least one package customers can book (e.g. Basic, Premium).
        </FormHelperText>

        <Stack gap={2}>
          {packages.map((pkg, index) => (
            <Box
              key={index}
              border={`1px solid ${COLORS.grey6}`}
              borderRadius={1}
              p={2}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                gap={2}
                alignItems='flex-start'
              >
                <TextField
                  fullWidth
                  size='small'
                  placeholder='Package name'
                  value={pkg.name}
                  onChange={e => handlePackageChange(index, 'name', e.target.value)}
                  error={Boolean(packageErrors[index]?.name)}
                  helperText={packageErrors[index]?.name}
                />
                <TextField
                  size='small'
                  placeholder='Price'
                  type='number'
                  inputProps={{ min: 0 }}
                  value={pkg.price}
                  onChange={e => handlePackageChange(index, 'price', e.target.value)}
                  error={Boolean(packageErrors[index]?.price)}
                  helperText={packageErrors[index]?.price}
                  sx={{ width: { xs: '100%', sm: 160 } }}
                />
                {packages.length > 1 && (
                  <IconButton
                    aria-label='Remove package'
                    onClick={() => removePackage(index)}
                    sx={{ mt: 0.25 }}
                  >
                    <DeleteOutlineIcon htmlColor={COLORS.black} />
                  </IconButton>
                )}
              </Stack>
              <TextField
                fullWidth
                size='small'
                multiline
                rows={2}
                placeholder='Package description (optional)'
                value={pkg.description}
                onChange={e =>
                  handlePackageChange(index, 'description', e.target.value)
                }
                sx={{ mt: 2 }}
              />
            </Box>
          ))}
        </Stack>

        <Button
          color='blue'
          variant='text'
          onClick={addPackage}
          startIcon={<AddIcon />}
          sx={{ mt: 1 }}
        >
          Add another package
        </Button>
      </Box>

      <Button
        color='blue'
        type='submit'
        disabled={isUploading}
        sx={{ display: 'table', mt: 2, px: 8, mx: 'auto' }}
      >
        {isUploading ? 'Uploading…' : 'Continue'}
      </Button>
    </Container>
  );
};

const schema = object({
  categoryId: string().required().label('Category'),
  hotlineNumber: string().required().label('Hotline Number'),
  location: string().required().label('Location'),
  website: string().url().label('Service Website URL'),
  facebookURL: string().url().label('Service SocialMedia URL'),
  description: string()
    .required()
    .min(50)
    .max(150)
    .label('Service Description'),
  images: array()
    .required()
    .min(1, 'At least one service image is required')
    .label('Service Images'),
});

const ERROR_ACTION = Object.freeze({
  SET: 'SET',
  CLEAR: 'CLEAR',
});

function errorReducer(state, { type, payload }) {
  switch (type) {
    case ERROR_ACTION.SET:
      return { ...state, ...payload };
    case ERROR_ACTION.CLEAR:
      return Object.keys(state).reduce(
        (errors, key) => ({ ...errors, [key]: null }),
        {}
      );
    default:
      return state;
  }
}

export default JobInformation;
